import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, input, Input, OnInit, ViewChild, inject } from '@angular/core';

import { UiChartData, UiPieChartQueryConfig, UiPieChartQueryRoute } from '@uigy/shared/components/ui-pie-chart/interfaces';
import { UiPieChartComponent } from '@uigy/shared/components/ui-pie-chart/ui-pie-chart.component';
import { UiSelectComponent } from '@uigy/shared/components/ui-inputs/ui-select/ui-select.component';
import { MenuItem } from '@uigy/shared/models/interfaces';
import { HeadingComponent } from '@uigy/shared/components/heading/heading.component';
import { DEFAULT_WIDTH, GAP_OF_MENU_FROM_BOTTOM } from '@uigy/shared/components/ui-pie-chart/ui-pie-chart.constants';

@Component({
    selector: 'app-ui-pie-chart-card',
    templateUrl: './ui-pie-chart-card.component.html',
    styleUrls: ['./ui-pie-chart-card.component.scss'],
    imports: [UiPieChartComponent, UiSelectComponent, HeadingComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiPieChartCardComponent implements OnInit, AfterViewInit {
  private cdr = inject(ChangeDetectorRef);

  @Input() title: string = '';
  @Input() transparentIconColor = false;
  @Input() readonly dropdownOptions?: MenuItem[];
  @Input() readonly queryConfig?: UiPieChartQueryConfig;
  // Pre-fetched data path: when provided, the card renders this directly instead
  // of fetching via a query/dropdown (used by ot-agent's control panel).
  @Input() readonly chartData?: UiChartData[];

  @ViewChild('cardContainer') cardContainer?: ElementRef;
  @ViewChild('selectComponent') selectComponent?: ElementRef;

  public menuMaxHeight: string = 'auto';
  public query?: UiPieChartQueryRoute;
  public pieChartWidth: number = DEFAULT_WIDTH;

  public isClickable = input<boolean>(true);
  public showSelect = input<boolean>(true);

  ngOnInit(): void {
    const selectedOption = this.dropdownOptions?.find(option => option.selected);
    if (selectedOption) {
      this.onSelectionChange(selectedOption);
    } else {
      if (this.dropdownOptions) {
        this.onSelectionChange(this.dropdownOptions[0]);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.cardContainer) {
      this.calcPieChartComponentWidth();
      // The select may be hidden (showSelect=false), so only size its menu when present.
      if (this.selectComponent) {
        this.calcSelectMenuMaxHeight();
      }
    }
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.calcPieChartComponentWidth();
  }

  // this is used in order to make sure the dropdown height is less than the height
  // of the card
  private calcSelectMenuMaxHeight() {
    if (this.cardContainer && this.selectComponent) {
      const cardWrapperRect = this.cardContainer.nativeElement.getBoundingClientRect();
      const selectComponentRect = this.selectComponent.nativeElement.getBoundingClientRect();

      // Calculate the top position of the select component relative to the cardWrapper
      const selectComponentTopRelativeToCard = selectComponentRect.top - cardWrapperRect.top;

      const selectComponentHeight = this.selectComponent.nativeElement.offsetHeight;

      // Calculate the bottom position of the select component relative to the cardWrapper
      const selectComponentBottomRelativeToCard = selectComponentTopRelativeToCard + selectComponentHeight;

      const cardWrapperHeight = this.cardContainer.nativeElement.offsetHeight;
      const availableHeight = cardWrapperHeight - selectComponentBottomRelativeToCard;
      this.menuMaxHeight = `${availableHeight - GAP_OF_MENU_FROM_BOTTOM}px`;
    }
  }

  private calcPieChartComponentWidth() {
    if (this.cardContainer) {
      // calc width for rendering the pie chart part
      const cardWidth = this.cardContainer.nativeElement.offsetWidth;
      const padding = parseFloat(getComputedStyle(this.cardContainer.nativeElement).padding);
      this.pieChartWidth = cardWidth - (2 * padding);
      this.cdr.detectChanges();
    }
  }

  public onSelectionChange(selectedItem: MenuItem): void {
    this.query = selectedItem.value;
  }
}
